import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Card,
    CardContent,
    Link,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import mapImg from '../../assets/map_img.png'; // 임시 지도 이미지

const contactInfoData = [
    {
        icon: <LocationOnOutlinedIcon sx={{ color: '#104e7a' }} />,
        title: 'Address',
        desc: (
            <>
                대구광역시 수성구 교학로 11길 46(만촌우방타운)
                <br />
                302동 1층 107호
            </>
        ),
    },
    {
        icon: <EmailOutlinedIcon sx={{ color: '#104e7a' }} />,
        title: 'Email',
        desc: 'psych@garam-mind.com',
    },
    {
        icon: <PhoneOutlinedIcon sx={{ color: '#104e7a' }} />,
        title: 'Phone',
        desc: '010-9424-3355',
    },
    {
        icon: <AccessTimeOutlinedIcon sx={{ color: '#104e7a' }} />,
        title: 'Business Hours',
        desc: '월-금 9:00 AM - 6:00 PM',
    },
];

export default function ContactPage() {
    // const mapElement = useRef(null);
    const [formData, setFormData] = useState({
        region: '',
        name: '',
        email: '',
        phone: '',
        content: '',
        agreed: false,
    });

    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
    const hasEmailError = formData.email.trim() !== '' && !isEmailValid;

    const isFormValid =
        formData.region.trim() !== '' &&
        formData.name.trim() !== '' &&
        isEmailValid &&
        formData.phone.trim() !== '' &&
        formData.content.trim() !== '' &&
        formData.agreed === true;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async () => {
        if (!isFormValid) return;

        try {
            const payload = {
                company: formData.region,
                manager_name: formData.name,
                email: formData.email,
                phone: formData.phone,
                content: formData.content,
                privacy_agreed: true,
            };

            await axios.post(`${process.env.REACT_APP_SERVER}/api/contact/submit`, payload);
            alert('문의가 성공적으로 접수되었습니다. 빠르게 답변드리겠습니다.');

            // 전송 완료 후 폼 초기화
            setFormData({
                region: '',
                name: '',
                email: '',
                phone: '',
                content: '',
                agreed: false,
            });
        } catch (error) {
            console.error('Contact submit error:', error);
            alert('전송 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
        }
    };

    useEffect(() => {
        // 네이버 지도 API 인증 오류로 임시로 이미지 대체
        /*
        const { naver } = window;
        if (!mapElement.current || !naver || !naver.maps) return;

        // 대구광역시 수성구 교학로 11길 46의 좌표 (대략적인 위치)
        const location = new naver.maps.LatLng(35.8498529, 128.6558412);
        const mapOptions = {
            center: location,
            zoom: 17, // 확대 레벨
            zoomControl: true, // 줌 컨트롤 표시
        };

        const map = new naver.maps.Map(mapElement.current, mapOptions);

        // 마커 추가
        new naver.maps.Marker({
            position: location,
            map: map,
            title: '가람마인드',
        });
        */
    }, []);

    return (
        <Box sx={{ bgcolor: '#ffffff', py: { xs: 8, md: 12 } }}>
            <Container maxWidth="lg">
                {/* 상단 타이틀 영역 */}
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
                        Contact Support
                    </Typography>
                    <Typography variant="h6" fontWeight="normal">
                        궁금한 점이나 협업 문의가 있으시면 언제든 편하게 연락 주세요.
                    </Typography>
                </Box>

                {/* 메인 2단 레이아웃 (Grid 대신 Flexbox 사용) */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' }, // 모바일은 세로, 데스크톱은 가로
                        gap: 4,
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Card
                            elevation={0}
                            sx={{ border: '1px solid #e0e0e0', borderRadius: 4, height: '100%' }}
                        >
                            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                    문의사항
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                    아래 양식을 작성해 주시면 빠르게 연락드리겠습니다.
                                </Typography>

                                {/* 폼 내부 레이아웃 */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* 1행: 지역/회사명 & 성함 (나란히 배치) */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            gap: 2,
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                지역/회사명 <span style={{ color: 'red' }}>*</span>
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                name="region"
                                                value={formData.region}
                                                onChange={handleChange}
                                                placeholder="지역/회사명을 입력하세요."
                                                variant="outlined"
                                                size="small"
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                성함 <span style={{ color: 'red' }}>*</span>
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="성함을 입력하세요."
                                                variant="outlined"
                                                size="small"
                                            />
                                        </Box>
                                    </Box>

                                    {/* 2행: 이메일 */}
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight="bold"
                                            sx={{ mb: 1 }}
                                        >
                                            이메일 <span style={{ color: 'red' }}>*</span>
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="이메일을 입력해주세요."
                                            variant="outlined"
                                            size="small"
                                            error={hasEmailError}
                                            helperText={
                                                hasEmailError
                                                    ? '올바른 이메일 형식을 입력해주세요.'
                                                    : ''
                                            }
                                        />
                                    </Box>

                                    {/* 3행: 연락처 */}
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight="bold"
                                            sx={{ mb: 1 }}
                                        >
                                            연락처 <span style={{ color: 'red' }}>*</span>
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="연락처를 입력해주세요."
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Box>

                                    {/* 4행: 문의내용 */}
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight="bold"
                                            sx={{ mb: 1 }}
                                        >
                                            문의내용 <span style={{ color: 'red' }}>*</span>
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={6}
                                            name="content"
                                            value={formData.content}
                                            onChange={handleChange}
                                            placeholder="문의하실 내용을 입력해주세요."
                                            variant="outlined"
                                        />
                                    </Box>
                                </Box>

                                {/* 개인정보 동의 체크박스 */}
                                <Box sx={{ mt: 3, mb: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                name="agreed"
                                                checked={formData.agreed}
                                                onChange={handleChange}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" color="text.secondary">
                                                <Link
                                                    component={RouterLink}
                                                    to="/privacy-policy"
                                                    underline="always"
                                                    color="#045584"
                                                    fontWeight="bold"
                                                >
                                                    개인정보 처리방침
                                                </Link>
                                                에 동의합니다.
                                            </Typography>
                                        }
                                    />
                                </Box>

                                {/* 전송 버튼 */}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={!isFormValid}
                                    onClick={handleSubmit}
                                    sx={{
                                        bgcolor: '#104e7a',
                                        py: 1.5,
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        borderRadius: 2,
                                        '&:hover': { bgcolor: '#0c3a5c' },
                                    }}
                                >
                                    문의사항 보내기
                                </Button>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* 연락처 정보 카드 */}
                        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 4 }}>
                                    연락처 정보
                                </Typography>

                                {/* flexWrap을 이용한 2x2 배치 */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        rowGap: 4,
                                        columnGap: 2,
                                    }}
                                >
                                    {contactInfoData.map((info, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                width: { xs: '100%', sm: 'calc(50% - 16px)' },
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1.5,
                                            }}
                                        >
                                            {info.icon}
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight="bold"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    {info.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ wordBreak: 'keep-all' }}
                                                >
                                                    {info.desc}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                        {/* 네이버 지도 영역 (인증 오류로 임시 이미지 대체) */}
                        <Box
                            sx={{
                                flex: 1, // 남은 세로 공간 채우기
                                minHeight: { xs: '300px', md: '400px' },
                                backgroundColor: '#f5f5f5',
                                borderRadius: 4,
                                overflow: 'hidden',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            <img
                                src={mapImg}
                                alt="오시는 길 지도"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
